import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

const UAE_EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const { profile, updateProfile, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "+971",
    address_line1: "",
    address_line2: "",
    city: "Dubai",
    emirate: "Dubai",
    country: "United Arab Emirates",
    postal_code: "",
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "+971",
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        city: profile.city || "Dubai",
        emirate: profile.emirate || "Dubai",
        country: profile.country || "United Arab Emirates",
        postal_code: profile.postal_code || "",
      });
    }
  }, [profile]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await updateProfile(formData);

    if (error) {
      toast.error("Failed to update profile");
    } else {
      toast.success("Profile updated successfully!");
      navigate("/chat");
    }

    setLoading(false);
  };

  const handleSkip = () => {
    navigate("/chat");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex flex-col">
          <h1 className="font-serif text-xl font-semibold text-foreground">
            Complete Your Profile
          </h1>
          <p className="text-sm text-muted-foreground">
            Help us personalize your experience
          </p>
        </div>
        <button
          onClick={handleSkip}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          Skip for now
        </button>
      </header>

      {/* Form */}
      <main className="flex-1 px-6 pb-8 overflow-auto">
        <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
          {/* Name Section */}
          <section className="space-y-4">
            <h3 className="font-serif text-lg font-medium text-foreground border-b border-border pb-2">
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+971 50 123 4567"
              />
            </div>
          </section>

          {/* Address Section */}
          <section className="space-y-4">
            <h3 className="font-serif text-lg font-medium text-foreground border-b border-border pb-2">
              Address
            </h3>

            <div className="space-y-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                placeholder="Building name, Street"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
              <Input
                id="address_line2"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                placeholder="Apartment, Suite, Floor"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Dubai"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emirate">Emirate</Label>
                <select
                  id="emirate"
                  name="emirate"
                  value={formData.emirate}
                  onChange={handleChange}
                  className="flex h-11 w-full rounded-lg border border-input bg-card px-4 py-2 text-base text-foreground shadow-soft transition-all duration-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {UAE_EMIRATES.map((emirate) => (
                    <option key={emirate} value={emirate}>
                      {emirate}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled
                  className="opacity-70"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code (Optional)</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="00000"
                />
              </div>
            </div>
          </section>

          <Button
            type="submit"
            variant="gold"
            size="lg"
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>Continue to Chat</span>
                <ArrowRight size={18} />
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
